import type { MusicContextType } from '@tunasong/music-lib'
import { useCallback } from 'react'
import invariant from 'tiny-invariant'
import { editorsSlice } from '../features/editors/editors-slice.js'

import { useSelector } from './selector.hook.js'
import { useThunkDispatch } from './thunk-dispatch.hook.js'

interface UseMusicContextProps<T> {
  /** If editorId is not specified, the default context values are given */
  editorId?: string | null
  selector: (context: MusicContextType) => T | undefined
}
export const useMusicContext = <T>({ editorId, selector }: UseMusicContextProps<T>) => {
  const value: T | undefined = useSelector(state => {
    const id = editorId ?? 'default'
    const context = state.editors[id]?.context
    return context ? selector(context) : undefined
  })

  const dispatch = useThunkDispatch()
  const setContext = useCallback(
    (context: Partial<MusicContextType>) => {
      invariant(editorId, 'editorId must be specified')
      dispatch(editorsSlice.actions.setContext({ id: editorId, context }))
    },
    [dispatch, editorId]
  )

  /**
   * Evict this editor's context entry (GH #1628). The owning editor calls this
   * on unmount so the chord/section context — which references document
   * subtrees — does not accumulate in the store forever as the user switches
   * songs. No-op for the shared `default` context.
   */
  const clearContext = useCallback(() => {
    if (!editorId || editorId === 'default') {
      return
    }
    dispatch(editorsSlice.actions.clearContext({ id: editorId }))
  }, [dispatch, editorId])

  return [value, setContext, clearContext] as const
}
